import {
  Account,
  ComplianceEntity,
  InternalAccount
} from "@wingspanhq/wingspan-api-sdk";
import { selectorIsBusinessTypeOnePerson } from "../../../shared/selectors/selectorIsBusinessTypeOnePerson";
import { selectorCanEnableTaxWithholdings } from "./selectorCanEnableTaxWithholdings";

/**
 * Whether to offer tax withholdings to this account.
 *
 * v1 read the payee context off `user.contexts` and the company structure off
 * the member profile. Both are retired: the payee-only check is now the
 * caller's scope gate (`bookkeeping.withholdingTransaction`, the same one the
 * side nav uses), which it passes in as `hasWithholdingsAccess`, and the
 * structure comes from the v3 account profile.
 */
export const selectorProposeTaxWithholdings = (params: {
  account?: Account;
  monitoredEntity?: ComplianceEntity;
  internalAccounts?: InternalAccount[];
  isTaxWithholdingEnabled?: boolean;
  hasWithholdingsAccess?: boolean;
}) => {
  const { account, monitoredEntity, internalAccounts } = params;

  if (!account) {
    return false;
  }

  // Don't show a promotion if user already has tax withholding account in any state. It means they already opted-in
  const hasInternalAccount = internalAccounts?.some(
    internalAccount => internalAccount.type === "TaxWithholding"
  );

  if (hasInternalAccount) {
    return false;
  }

  // Don't show promotions to payers, only payees should see it
  if (!params.hasWithholdingsAccess) {
    return false;
  }

  // Don't show promotions to users who can't enable tax withholdings (US only)
  if (!selectorCanEnableTaxWithholdings(monitoredEntity)) {
    return false;
  }

  // Don't show promotions to users who are not single person business
  const companyStructure = account.profile?.company?.structure;

  if (!selectorIsBusinessTypeOnePerson(companyStructure)) {
    return false;
  }

  // Finally, check if user has tax withholding enabled
  return !params.isTaxWithholdingEnabled;
};
